/*
 * Copyright (c) 2023. Haodong JU
 */

import { setHouseRatesColumn } from '@/model/dataLayer/api/report/report'
import dayjs from 'dayjs'
import {
  getFixedCost,
  getFixedDefaultCostList,
  getFixedExtraCostList,
  getPurchasingCost,
  getPurchasingDefaultCostList,
  getReportDate
} from '@/model/dataLayer/api/report/salesReportApi'
import { groupBy, sumBy } from 'lodash'
import { getInOutLog } from '@/model/dataLayer/api/storage/inventory'
import hillo from 'hillo'
import i18n from '@/i18n'

export async function getAllCostWithOutStorage (timespan) {
  const res = setHouseRatesColumn(timespan)
  const defaultPurchasingList = (await getPurchasingDefaultCostList())
  const defaultFixedList = (await getFixedDefaultCostList())
  const defaultFixedCost = sumBy(defaultFixedList, function (o) {
    return parseFloat(o.value)
  })
  const defaultPersonCost = sumBy(defaultFixedList.slice(0, 2), function (o) {
    return parseFloat(o.value)
  })
  const defaultHouseCost = defaultFixedList[2]?.value ?? 0
  const defaultHydropowerCost = defaultFixedList[3]?.value ?? 0
  const defaultPurchasingCost = sumBy(defaultPurchasingList, function (o) {
    return parseFloat(o.value)
  })
  const defaultTotal = defaultFixedCost + defaultPurchasingCost
  const historyReportList = await getReportDate()
  const realSelectedHistoryReportCost = []

  for (const item of res) {
    const currentMonth = dayjs(item).format('YYYY-MM')
    if (historyReportList.includes(currentMonth)) {
      const fixedList = await getFixedCost(currentMonth)
      const fixedCost = sumBy(fixedList, function (o) {
        return parseFloat(o.value)
      })
      const personCost = sumBy(fixedList.slice(0, 2), function (o) {
        return parseFloat(o.value)
      })
      const houseCost = fixedList[2]?.value ?? 0
      const hydropowerCost = fixedList[3]?.value ?? 0
      const purchasingCost = sumBy(await getPurchasingCost(currentMonth), function (o) {
        return parseFloat(o.value)
      })
      const dayLength = dayjs(currentMonth).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: (purchasingCost / dayLength).toFixed(2),
        dailyFixed: (fixedCost / dayLength).toFixed(2),
        dailyTotal: ((purchasingCost + fixedCost) / dayLength).toFixed(2),
        dailyPerson: (personCost / dayLength).toFixed(2),
        dailyHouse: (houseCost / dayLength).toFixed(2),
        dailyHydropower: (hydropowerCost / dayLength).toFixed(2)
      })
    } else {
      const dayLength = dayjs(currentMonth).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: (defaultPurchasingCost / dayLength).toFixed(2),
        dailyFixed: (defaultFixedCost / dayLength).toFixed(2),
        dailyTotal: (defaultTotal / dayLength).toFixed(2),
        dailyPerson: (defaultPersonCost / dayLength).toFixed(2),
        dailyHouse: (defaultHouseCost / dayLength).toFixed(2),
        dailyHydropower: (defaultHydropowerCost / dayLength).toFixed(2)
      })
    }
  }
  return realSelectedHistoryReportCost
}

export async function getAllCostWithStorage (timespan) {
  const storageFromTime = timespan[0] + 'T04:00:00'
  const storageToTime = dayjs(timespan[1]).add(1, 'day').format('YYYY-MM-DD') + 'T03:59:59'
  const materialOutList = (await getInOutLog(storageFromTime, storageToTime)).map(it => {
    it.changeLog.createdAt = dayjs(it.changeLog.createdAt).format('YYYY-MM-DD')
    return it.changeLog
  }).filter(x => x.operation === 'Out') ?? []
  const listByDate = groupBy(materialOutList, 'createdAt')
  const realCost = Object.fromEntries(Object.entries(listByDate).map(it => {
    it[1] = Math.abs(sumBy(it[1], 'costLiteral'))
    return it
  }))
  const historyReportList = await getReportDate()
  const defaultFixedList = [
    (await getFixedDefaultCostList()),
    await getFixedExtraCostList()
  ].flat()
  const defaultFixedCost = sumBy(defaultFixedList, function (o) {
    return parseFloat(o.value)
  })
  const defaultPersonCost = sumBy(defaultFixedList.slice(0, 2), function (o) {
    return parseFloat(o.value)
  })
  const defaultHouseCost = defaultFixedList[2]?.value ?? 0
  const defaultHydropowerCost = defaultFixedList[3]?.value ?? 0
  const realSelectedHistoryReportCost = []
  const res = setHouseRatesColumn(timespan)
  for (const item of res) {
    const currentMonth = dayjs(item).format('YYYY-MM')
    const thisMonth = dayjs().format('YYYY-MM')
    if (historyReportList.includes(currentMonth) && currentMonth !== thisMonth) {
      const fixedList = await getFixedCost(currentMonth)
      const fixedCost = sumBy(fixedList, function (o) {
        return parseFloat(o.value)
      })
      const personCost = sumBy(fixedList.slice(0, 2), function (o) {
        return parseFloat(o.value)
      })
      const houseCost = fixedList[2]?.value ?? 0
      const hydropowerCost = fixedList[3]?.value ?? 0
      const dayLength = dayjs(currentMonth).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: realCost[item] ?? 0,
        dailyFixed: fixedCost / dayLength,
        dailyTotal: ((realCost[item] ?? 0) + (fixedCost / dayLength)).toFixed(2),
        dailyPerson: (personCost / dayLength).toFixed(2),
        dailyHouse: (houseCost / dayLength).toFixed(2),
        dailyHydropower: (hydropowerCost / dayLength).toFixed(2)
      })
    } else {
      const dayLength = dayjs(item).daysInMonth()
      realSelectedHistoryReportCost.push({
        date: item,
        dailyPurchasing: realCost[item] ?? 0,
        dailyFixed: defaultFixedCost / dayLength,
        dailyTotal: ((realCost[item] ?? 0) + (defaultFixedCost / dayLength)).toFixed(2),
        dailyPerson: (defaultPersonCost / dayLength).toFixed(2),
        dailyHouse: (defaultHouseCost / dayLength).toFixed(2),
        dailyHydropower: (defaultHydropowerCost / dayLength).toFixed(2)
      })
    }
  }
  return realSelectedHistoryReportCost
}

export async function salesInsight (timeSpan, hourStart, hourEnd) {
  if (!timeSpan) {
    return []
  }
  if (!timeSpan.length || timeSpan.length < 2) {
    return []
  }
  const [timeStart, timeEnd] = timeSpan
  return (await hillo.get('Orders.php?op=saleAnalysis', {
    timeStart,
    timeEnd,
    hourStart,
    hourEnd,
    lang: i18n.locale.toUpperCase()
  })).content
}
